export type TaggableEntityType = 'project' | 'task';
export type EntityTagColors = Record<string, string>;

export const MAX_ENTITY_TAGS = 20;
export const MAX_ENTITY_TAG_LENGTH = 40;
export const MAX_ENTITY_TAG_COLOR_DEFINITIONS = 200;
export const DEFAULT_ENTITY_TAG_COLOR = '#64748b';
export const ENTITY_TAG_DEFINITIONS_DOCUMENT_ID = 'definitions';

export function normalizeTagLabel(value: unknown): string {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, MAX_ENTITY_TAG_LENGTH);
}
export function tagKey(value: unknown): string {
    return normalizeTagLabel(value).toLocaleLowerCase();
}

export function normalizeEntityTagColor(value: unknown): string {
    const color = String(value || '').trim().toLowerCase();
    return /^#[0-9a-f]{6}$/.test(color) ? color : DEFAULT_ENTITY_TAG_COLOR;
}

export function normalizeEntityTagColors(values: unknown): EntityTagColors {
    if (!values || typeof values !== 'object' || Array.isArray(values)) return {};

    const normalized: EntityTagColors = {};
    for (const [label, color] of Object.entries(values as Record<string, unknown>)) {
        const key = tagKey(label);
        if (!key) continue;
        normalized[key] = normalizeEntityTagColor(color);
        if (Object.keys(normalized).length >= MAX_ENTITY_TAG_COLOR_DEFINITIONS) break;
    }
    return normalized;
}

export function resolveEntityTagColor(tag: unknown, colors: EntityTagColors = {}): string {
    return normalizeEntityTagColor(colors[tagKey(tag)] || DEFAULT_ENTITY_TAG_COLOR);
}

export function normalizeEntityTags(values: unknown, limit = MAX_ENTITY_TAGS): string[] {
    if (!Array.isArray(values)) return [];

    const normalized: string[] = [];
    const seen = new Set<string>();

    for (const value of values) {
        const label = normalizeTagLabel(value);
        const key = tagKey(label);
        if (!label || seen.has(key)) continue;

        normalized.push(label);
        seen.add(key);
        if (normalized.length >= Math.max(0, limit)) break;
    }

    return normalized;
}

export function collectEntityTagSuggestions(tagGroups: Array<string[] | undefined | null>): string[] {
    return normalizeEntityTags(tagGroups.flatMap(tags => tags || []), Number.MAX_SAFE_INTEGER)
        .sort((a, b) => a.localeCompare(b));
}

export function entityTagDocumentId(entityType: TaggableEntityType, entityId: string): string {
    const normalizedId = String(entityId || '').trim();
    if (!normalizedId || normalizedId.includes('/')) {
        throw new Error('A valid entity ID is required to manage tags.');
    }
    return `${entityType}_${normalizedId}`;
}
